import { gsap } from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

let smoother = ScrollSmoother;

const menuToggle = document.getElementById('menu-toggle');
const mobileMenu = document.getElementById('mobile-menu');
const line1 = document.getElementById('line1');
const line2 = document.getElementById('line2');
const line3 = document.getElementById('line3');
const mobileMenuLinks = mobileMenu?.querySelectorAll('a');

function closeMenu() {
  if (menuToggle && mobileMenu && line1 && line2 && line3) {
    mobileMenu.style.height = '0';
    line1.style.transform = 'rotate(0) translate(0, 0)';
    line2.style.opacity = '1';
    line3.style.transform = 'rotate(0) translate(0, 0)';
    menuToggle.setAttribute('aria-expanded', 'false');
  }
}

if (menuToggle && mobileMenu && line1 && line2 && line3) {
  menuToggle.addEventListener('click', () => {
    const isExpanded = menuToggle.getAttribute('aria-expanded') === 'true';

    if (!isExpanded) {
      mobileMenu.style.height = `${mobileMenu.firstElementChild?.offsetHeight}px`;
      line1.style.transform = 'rotate(45deg) translate(2.5px, 2.5px)';
      line2.style.opacity = '0';
      line3.style.transform = 'rotate(-45deg) translate(2.5px, -2.5px)';
    } else {
      closeMenu();
    }

    menuToggle.setAttribute('aria-expanded', (!isExpanded).toString());
  });
}

// Add click handlers to all mobile menu links
if (mobileMenuLinks) {
  mobileMenuLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      // Close menu after a small delay to allow the click to process
      setTimeout(closeMenu, 150);
    });
  });
}

window.addEventListener('DOMContentLoaded', () => {
  // Check if the device has touch capability
  const isTouchDevice =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;

  if (!isTouchDevice) {
    smoother = ScrollSmoother.create({
      wrapper: '#smooth-wrapper',
      content: '#smooth-content',
      smooth: 1.15,
      effects: true,
      normalizeScroll: true,
      ignoreMobileResize: true,
    });

    // Handle initial hash in URL
    const initialHash = window.location.hash;
    if (initialHash) {
      const targetElement = document.querySelector(initialHash);
      if (targetElement) {
        setTimeout(() => {
          smoother.scrollTo(targetElement, false);
        }, 100);
      }
    }

    // Handle both /#id and #id links
    document.querySelectorAll('a[href*="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        if (!href) return;

        const isRootHash = href.startsWith('/#');
        const currentPath = window.location.pathname;
        const hashPart = href.includes('#') ? href.split('#')[1] : '';

        if (
          (isRootHash && currentPath === '/') ||
          (!isRootHash && href.startsWith('#'))
        ) {
          e.preventDefault();
          const target = document.querySelector(`#${hashPart}`);
          if (target) {
            smoother.scrollTo(target, true);
          }
        }
      });

      anchor.addEventListener('mousedown', (e) => {
        const href = anchor.getAttribute('href');
        if (href?.includes('#')) {
          e.preventDefault();
        }
      });
    });
  } else {
    document
      .querySelector('#smooth-wrapper')
      ?.setAttribute(
        'style',
        'position: static; overflow: visible; height: auto;'
      );
  }

  // Initialize text animations
  function initTextAnimations() {
    // Hero section animations
    gsap.from('.hero-text', {
      opacity: 0.1,
      y: 30,
      duration: 1.5,
      stagger: 0.4,
      ease: 'power2.out',
    });

    // What we do section text animations
    gsap.from('.animate-text', {
      scrollTrigger: {
        trigger: '#what-we-do',
        start: 'top 70%',
        toggleActions: 'play none none reset',
      },
      opacity: 0.1,
      y: 30,
      duration: 1.5,
      stagger: 0.5,
      ease: 'power2.out',
    });

    // What we do cards animations
    gsap.from('.animate-card', {
      scrollTrigger: {
        trigger: '#what-we-do ul',
        start: 'top 75%',
        toggleActions: 'play none none reset',
      },
      opacity: 0.1,
      y: 40,
      duration: 1.4,
      stagger: 0.3,
      ease: 'power2.out',
    });

    // ROOST acronym animations
    const roostTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: '#who-we-are',
        start: 'top 60%',
        toggleActions: 'play none none reset',
      },
    });

    // Select all letter elements (first p tag in each flex container)
    const letters = document.querySelectorAll(
      '#who-we-are .flex-1 > div:first-child p'
    );
    // Select all word elements (second p tag in each flex container)
    const words = document.querySelectorAll(
      '#who-we-are .flex-1 > div:last-child p'
    );

    // Set initial states
    gsap.set(letters, { opacity: 0, y: 20 });
    gsap.set(words, { opacity: 0, x: -20 });

    // Animate letters first
    roostTimeline.to(letters, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      stagger: 0.2,
      ease: 'power4.out',
    });

    // Then animate words
    roostTimeline.to(
      words,
      {
        opacity: 1,
        x: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power3.out',
      },
      '-=0.6'
    ); // Start slightly before the letters finish
  }

  // Initialize all animations
  initTextAnimations();

  // Press items pagination
  const itemsPerPage = 4;
  const pressItems = document.querySelectorAll('.press-item');

  if (pressItems.length > 0) {
    const totalPages = Math.ceil(pressItems.length / itemsPerPage);
    let currentPage = 1;

    function updateButtons() {
      const prevBtn = document.getElementById('prevBtn');
      const nextBtn = document.getElementById('nextBtn');
      if (prevBtn) prevBtn.disabled = currentPage === 1;
      if (nextBtn) nextBtn.disabled = currentPage === totalPages;
    }

    function updatePageDisplay() {
      const currentPageEl = document.getElementById('currentPage');
      const totalPagesEl = document.getElementById('totalPages');
      if (currentPageEl) currentPageEl.textContent = currentPage;
      if (totalPagesEl) totalPagesEl.textContent = totalPages;
      updateButtons();
    }

    function showPage(page) {
      const start = (page - 1) * itemsPerPage;
      const end = start + itemsPerPage;

      pressItems.forEach((item, index) => {
        if (index >= start && index < end) {
          item.style.display = '';
        } else {
          item.style.display = 'none';
        }
      });
    }

    window.changePage = function (direction) {
      if (direction === 'next' && currentPage < totalPages) {
        currentPage++;
      } else if (direction === 'prev' && currentPage > 1) {
        currentPage--;
      }

      showPage(currentPage);
      updatePageDisplay();
    };

    // Initialize first page
    showPage(1);
    updatePageDisplay();
  }
});

function initLogoScroll() {
  const scrollTrack = document.querySelector('[data-ticker]');
  if (!scrollTrack) return;

  const totalWidth = scrollTrack.scrollWidth / 2;

  gsap.to(scrollTrack, {
    x: -totalWidth,
    duration: 100,
    ease: 'none',
    repeat: -1,
  });
}

initLogoScroll();

// Contact Form

// Helper function to get form data in the supported format
function getFormDataString(formEl) {
  var formData = new FormData(formEl);
  var data = [];

  for (var keyValue of formData) {
    data.push(
      encodeURIComponent(keyValue[0]) + '=' + encodeURIComponent(keyValue[1])
    );
  }

  return data.join('&');
}

// Wait for DOM to be ready
document.addEventListener('DOMContentLoaded', function () {
  // Fetch the form element
  var formEl = document.getElementById('roost-zap');

  if (!formEl) {
    // console.error("Contact form element not found");
    return;
  }

  const submitButton = formEl.querySelector('button[type="submit"]');

  // Function to validate form and update submit button state
  function validateForm() {
    const requiredFields = formEl.querySelectorAll('[required]');
    let isValid = true;

    requiredFields.forEach((field) => {
      if (!field.value.trim()) {
        isValid = false;
      }
    });

    if (submitButton) {
      submitButton.disabled = !isValid;
      // Optional: Add visual feedback with classes
      if (isValid) {
        submitButton.classList.remove('opacity-50', 'cursor-not-allowed');
      } else {
        submitButton.classList.add('opacity-50', 'cursor-not-allowed');
      }
    }
  }

  // Add input event listeners to all form fields
  const formFields = formEl.querySelectorAll('input, select, textarea');
  formFields.forEach((field) => {
    field.addEventListener('input', validateForm);
    field.addEventListener('change', validateForm);
  });

  // Initial validation
  validateForm();

  // Override the submit event
  formEl.addEventListener('submit', function (e) {
    e.preventDefault();

    // Disable form while submitting
    if (submitButton) submitButton.disabled = true;

    const formData = getFormDataString(formEl);

    var request = new XMLHttpRequest();

    request.addEventListener('load', function () {
      if (request.status === 303 || request.status === 200) {
        // Success
        formEl.reset();
        document.getElementById('success-message').classList.remove('hidden');
      } else {
        // Error
        document.getElementById('error-message').classList.remove('hidden');
      }

      // scroll to the top of the message.
      document.getElementById('roost-zap').classList.add('hidden');
      const y =
        document.getElementById('form-message').getBoundingClientRect().top +
        window.scrollY;
      window.scroll({
        top: parseInt(y - 160),
        behavior: 'smooth',
      });

      if (submitButton) submitButton.disabled = false;
    });

    request.addEventListener('error', function () {
      alert(
        'Sorry, there was an error sending your message. Please try again.'
      );
      if (submitButton) submitButton.disabled = false;
    });

    request.open('POST', formEl.action);
    request.setRequestHeader(
      'Content-Type',
      'application/x-www-form-urlencoded'
    );
    request.send(formData);
  });
});

// Add togglePosts function to the global scope (window object)
window.togglePosts = function () {
  const hiddenPosts = document.getElementById('hiddenPosts');
  const toggleButton = document.getElementById('toggleButton');
  const visiblePosts = document.getElementById('visiblePosts');

  if (hiddenPosts.classList.contains('hidden')) {
    // Expanding - show all posts in hiddenPosts
    hiddenPosts.classList.remove('hidden');

    // When expanding, make ALL posts in hiddenPosts visible
    hiddenPosts.querySelectorAll('.post-card').forEach((card) => {
      card.classList.remove('hidden', 'md:hidden', 'lg:hidden');
    });

    // Hide visiblePosts section completely to avoid duplication
    visiblePosts.classList.add('hidden');

    toggleButton.textContent = '- Show fewer partner posts';
  } else {
    // Collapsing - hide hiddenPosts and restore visiblePosts
    hiddenPosts.classList.add('hidden');
    visiblePosts.classList.remove('hidden');

    // Restore responsive visibility in visiblePosts
    visiblePosts.querySelectorAll('.post-card').forEach((card, index) => {
      // Reset all hide/show classes
      card.classList.remove(
        'hidden',
        'md:hidden',
        'lg:hidden',
        'md:flex',
        'lg:flex'
      );

      // Reapply the original display logic
      if (index > 0) {
        card.classList.add('hidden');
        card.classList.add('md:flex');
      }
      if (index > 1) {
        card.classList.add('md:hidden');
        card.classList.add('lg:flex');
      }
      if (index > 2) {
        card.classList.add('lg:hidden');
      }
    });

    toggleButton.textContent = '+ Expand partner posts';
  }
};
